import React, { useState, useEffect } from 'react';
import { Modal, ConfirmDialog, EmptyState, ActionBtn, SectionHeader, SearchInput, FormField, StatCard, Pagination } from '../components/common/UI';
import { toShamsi, formatCurrency, daysUntil, todayShamsi } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';
import { MdAdd, MdEdit, MdDelete, MdCampaign, MdAttachMoney, MdVisibility, MdReceipt, MdPrint } from '../utils/icons';
import { FaInstagram, FaYoutube, FaTiktok, FaFacebook, FaTwitter, FaLinkedin } from '../utils/icons';

const STATUS_CONFIG = {
  Active:    { label: 'فعال',      color: 'bg-green-100 text-green-700 border border-green-200' },
  Pending:   { label: 'در انتظار', color: 'bg-yellow-100 text-yellow-700 border border-yellow-200' },
  Completed: { label: 'تکمیل',     color: 'bg-blue-100 text-blue-700 border border-blue-200' },
  Cancelled: { label: 'لغو شده',   color: 'bg-red-100 text-red-700 border border-red-200' },
  Negotiating: { label: 'مذاکره',  color: 'bg-purple-100 text-purple-700 border border-purple-200' },
};

const PLATFORMS = ['Instagram', 'YouTube', 'TikTok', 'Facebook', 'Twitter', 'LinkedIn', 'دیگر'];

const PLATFORM_ICONS = {
  Instagram: <FaInstagram size={14} className="text-pink-600" />,
  YouTube: <FaYoutube size={14} className="text-red-600" />,
  TikTok: <FaTiktok size={14} className="text-gray-800" />,
  Facebook: <FaFacebook size={14} className="text-blue-600" />,
  Twitter: <FaTwitter size={14} className="text-sky-600" />,
  LinkedIn: <FaLinkedin size={14} className="text-blue-500" />,
};

const initialForm = {
  title: '', client: '', platform: 'Instagram', budget: '',
   status: 'Pending',
  description: '', contactPerson: '', contactPhone: '', cost: ''
};

export default function Sponsorship() {
  const { isManager, user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, totalBudget: 0, activeBudget: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [invoiceItem, setInvoiceItem] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [page, setPage] = useState(1); // پیج‌بندی (صفحه‌بندی سمت کلاینت)

  // بارگذاری دیتا از بک‌اند
  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPlatform) params.platform = filterPlatform;
      if (search) params.search = search;
      
      const res = await API.get('/sponsorships', { params });
      setCampaigns(res.data.data);
      setStats(res.data.stats);
    } catch (error) {
      toast.error('خطا در بارگذاری داده‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [filterStatus, filterPlatform, search]);

  useEffect(() => { setPage(1); }, [filterStatus, filterPlatform, search]);

  const openAdd = () => { setForm(initialForm); setEditItem(null); setShowModal(true); };
  const openEdit = (c) => { setForm({ ...c, startDate: c.startDate?.split('T')[0] || '', endDate: c.endDate?.split('T')[0] || '', cost: c.cost || '' }); setEditItem(c); setShowModal(true); };

  const handleSave = async () => {
    if (!form.title || !form.client) return toast.error('عنوان و مشتری ضروری است');
    try {
      if (editItem) {
        await API.put(`/sponsorships/${editItem._id}`, form);
        toast.success('کمپین بروزرسانی شد');
      } else {
        await API.post('/sponsorships', form);
        toast.success('کمپین اضافه شد');
      }
      setShowModal(false);
      fetchCampaigns();
    } catch (error) {
      toast.error('خطا در ذخیره سازی');
    }
  };

  const handleSponsorshipPayment = async () => {
    if (!paymentAmount || paymentAmount <= 0) return toast.error('مبلغ را وارد کنید');
    const newPaidAmount = (viewItem.paidAmount || 0) + Number(paymentAmount);
    try {
      await API.put(`/sponsorships/${viewItem._id}`, { paidAmount: newPaidAmount });
      toast.success('پرداخت ثبت شد');
      setShowPaymentModal(false);
      setPaymentAmount('');
      fetchCampaigns();
      setViewItem(null);
    } catch (err) {
      toast.error('خطا در پرداخت');
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/sponsorships/${deleteId}`);
      toast.success('کمپین حذف شد');
      setDeleteId(null);
      fetchCampaigns();
    } catch (error) {
      toast.error('خطا در حذف');
    }
  };

  if (loading && campaigns.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  const filtered = campaigns; // فیلتر در بک‌اند انجام شده
  const PAGE_SIZE = 10;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader
        title="اسپانسرشیپ شبکه های اجتماعی"
        subtitle={`${filtered.length} کمپین`}
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="کمپین جدید" />}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<MdCampaign size={18} />} label="کل کمپین‌ها" value={stats.total} color="blue" />
        <StatCard icon={<MdCampaign size={18} />} label="کمپین‌های فعال" value={stats.active} color="green" />
        <StatCard icon={<MdAttachMoney size={18} />} label="بودجه کل" value={formatCurrency(stats.totalBudget)} color="purple" />
        <StatCard icon={<MdAttachMoney size={18} />} label="بودجه فعال" value={formatCurrency(stats.activeBudget)} color="orange" />
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1"><SearchInput value={search} onChange={setSearch} placeholder="جستجوی کمپین، مشتری یا پلتفرم..." /></div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-input sm:w-40">
            <option value="">همه وضعیت‌ها</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)} className="form-input sm:w-40">
            <option value="">همه پلتفرم‌ها</option>
            {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="📱" title="کمپینی یافت نشد" subtitle="اولین کمپین اسپانسری خود را اضافه کنید"
          action={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="کمپین جدید" />} />
      ) : (
        <>
          {/* Desktop Table */}
          <div className="glass-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="premium-table">
                <thead>
                  <tr><th>عنوان</th><th>مشتری</th><th>پلتفرم</th><th>بودجه</th><th>شروع</th><th>پایان</th><th>مدت باقیمانده</th><th>وضعیت</th><th>عملیات</th></tr>
                </thead>
                <tbody>
                  {paged.map(c => {
                    const days = daysUntil(c.endDate);
                    const isCompleted = c.status === 'Completed';
                    return (
                      <tr key={c._id}>
                        <td>
                          <div className="font-semibold text-gray-800">{c.title}</div>
                          {c.contactPerson && <div className="text-xs text-gray-600 mt-0.5">👤 {c.contactPerson}</div>}
                        </td>
                        <td className="text-gray-600">{c.client}</td>
                        <td>
                          <span className="flex items-center gap-1.5">
                            {PLATFORM_ICONS[c.platform]}
                            <span className="text-gray-600 text-sm">{c.platform}</span>
                          </span>
                        </td>
                        <td className="text-green-600 font-medium">{formatCurrency(c.budget)}</td>
                        <td className="text-gray-500 text-sm">{toShamsi(c.startDate)}</td>
                        <td className="text-gray-500 text-sm">{toShamsi(c.endDate)}</td>
                        <td className="text-gray-500 text-sm">
                          {isCompleted ? (
                            <span className="text-green-600">تکمیل شده</span>
                          ) : days < 0 ? (
                            <span className="text-red-600">{Math.abs(days)} روز تأخیر</span>
                          ) : days === 0 ? (
                            <span className="text-yellow-600">امروز!</span>
                          ) : (
                            <span className="text-gray-500">{days} روز</span>
                          )}
                        </td>
                        <td><span className={`badge ${STATUS_CONFIG[c.status]?.color}`}>{STATUS_CONFIG[c.status]?.label}</span></td>
                        <td>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setViewItem(c)} className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-all"><MdVisibility size={15} /></button>
                            <button onClick={() => setInvoiceItem(c)} className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-all" title="فاکتور"><MdReceipt size={15} /></button>
                            {isManager && <button onClick={() => openEdit(c)} className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-all"><MdEdit size={15} /></button>}
                            {isManager && <button onClick={() => setDeleteId(c._id)} className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all"><MdDelete size={15} /></button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards (همون کد قبلی، فقط داده‌ها از campaigns میاد) */}
          <div className="md:hidden space-y-3">
            {paged.map(c => {
              const days = c.status === 'Active' ? daysUntil(c.endDate) : null;
              return (
                <div key={c._id} className="mobile-card">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-semibold text-gray-800">{c.title}</div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                        {PLATFORM_ICONS[c.platform]}
                        <span>{c.platform} • {c.client}</span>
                      </div>
                    </div>
                    <span className={`badge ${STATUS_CONFIG[c.status]?.color}`}>{STATUS_CONFIG[c.status]?.label}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div><span className="text-gray-600 text-xs">بودجه:</span><br /><span className="text-green-600 font-medium">{formatCurrency(c.budget)}</span></div>
                    <div><span className="text-gray-600 text-xs">پایان کمپین:</span><br /><span className="text-gray-600">{toShamsi(c.endDate)}</span></div>
                  </div>
                  {days !== null && (
                    <div className={`text-xs font-medium mb-2 ${days < 0 ? 'text-red-600' : days <= 7 ? 'text-yellow-600' : 'text-gray-500'}`}>
                      {days < 0 ? `⚠️ ${Math.abs(days)} روز تأخیر` : `⏱️ ${days} روز باقیمانده`}
                    </div>
                  )}
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => setInvoiceItem(c)} className="flex-1 py-1.5 text-xs bg-green-100 hover:bg-green-200 text-green-600 rounded-lg flex items-center justify-center gap-1"><MdReceipt size={14} /> فاکتور</button>
                    {isManager && <button onClick={() => openEdit(c)} className="flex-1 py-1.5 text-xs bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-lg">ویرایش</button>}
                    {isManager && <button onClick={() => setDeleteId(c._id)} className="flex-1 py-1.5 text-xs bg-red-100 text-red-600 rounded-lg">حذف</button>}
                  </div>
                </div>
              );
            })}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      {/* Add/Edit Modal - بدون تغییر */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'ویرایش کمپین' : 'کمپین جدید'} size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="عنوان کمپین" required>
            <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="عنوان کمپین اسپانسری" />
          </FormField>
          <FormField label="مشتری / برند" required>
            <input className="form-input" value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} placeholder="نام برند یا مشتری" />
          </FormField>
          <FormField label="پلتفرم">
            <select className="form-input" value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}>
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </FormField>
          <FormField label="وضعیت">
            <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </FormField>
          <FormField label="هزینه مشتری (افغانی)">
            <input type="number" className="form-input" value={form.budget} onChange={e => setForm({ ...form, budget: +e.target.value })} placeholder="0" />
          </FormField>
          <FormField label="هزینه تولید (افغانی)">
            <input type="number" className="form-input" value={form.cost} onChange={e => setForm({ ...form, cost: +e.target.value })} placeholder="0" />
          </FormField>
          <FormField label="مبلغ پرداخت شده (افغانی)">
            <input type="number" className="form-input" value={form.paidAmount || 0} onChange={e => setForm({...form, paidAmount: +e.target.value})} />
          </FormField>
          <FormField label=" تماس شخص">
            <input className="form-input" value={form.contactPerson} onChange={e => setForm({ ...form, contactPerson: e.target.value })} placeholder="نام شخص تماس" />
          </FormField>
          <FormField label="تاریخ شروع">
            <ShamsiDatePicker
              value={form.startDate}
              onChange={(date) => setForm({...form, startDate: date})}
              placeholder="1403/01/01"
              className="form-input w-full"
            />
          </FormField>
          <FormField label="تاریخ پایان">
            <ShamsiDatePicker
              value={form.endDate}
              onChange={(date) => setForm({...form, endDate: date})}
              placeholder="1403/12/29"
              className="form-input w-full"
            />
          </FormField>
          <FormField label="تلفن تماس">
            <input className="form-input" value={form.contactPhone} onChange={e => setForm({ ...form, contactPhone: e.target.value })} placeholder="07xx-xxx-xxx" />
          </FormField>
          <div className="sm:col-span-2">
            <FormField label="توضیح">
              <textarea className="form-input h-20 resize-none" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="توضیح کمپین..." />
            </FormField>
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
          <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-sm">انصراف</button>
          <ActionBtn onClick={handleSave} label={editItem ? 'بروزرسانی' : 'ذخیره'} />
        </div>
      </Modal>

{/* View Modal */}
{viewItem && (
  <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="جزئیات کامل کمپین" size="lg">
    <div className="space-y-5">
      <div className="flex items-start justify-between border-b border-gray-200 pb-3">
        <h2 className="text-xl font-bold text-gray-800">{viewItem.title}</h2>
        <div className="flex items-center gap-2">
          <span className={`badge ${STATUS_CONFIG[viewItem.status]?.color}`}>{STATUS_CONFIG[viewItem.status]?.label}</span>
          <ActionBtn onClick={() => { setInvoiceItem(viewItem); }} icon={<MdReceipt size={16} />} label="تولید فاکتور" variant="success" />
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-gray-600 mb-0.5">عنوان کمپین</div>
          <div className="text-gray-800 text-sm font-medium">{viewItem.title}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">مشتری / برند</div>
          <div className="text-gray-800 text-sm font-medium">{viewItem.client}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">پلتفرم</div>
          <div className="text-gray-800 text-sm font-medium flex items-center gap-1.5">
            {PLATFORM_ICONS[viewItem.platform]}
            {viewItem.platform}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">بودجه (درآمد)</div>
          <div className="text-green-600 text-sm font-bold">{formatCurrency(viewItem.budget)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">هزینه تولید</div>
          <div className="text-red-600 text-sm font-bold">{formatCurrency(viewItem.cost || 0)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">مبلغ پرداخت شده</div>
          <div className="text-blue-600 text-sm font-bold">{formatCurrency(viewItem.paidAmount || 0)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">باقی‌مانده</div>
          <div className="text-red-600 text-sm font-bold">{formatCurrency((viewItem.budget || 0) - (viewItem.paidAmount || 0))}</div>
        </div>
        {(viewItem.budget - (viewItem.paidAmount || 0)) > 0 && (
          <button onClick={() => setShowPaymentModal(true)} className="w-full mt-3 py-2 bg-green-600 rounded-xl text-white">
            پرداخت باقی‌مانده ({formatCurrency(viewItem.budget - (viewItem.paidAmount || 0))})
          </button>
        )}
        <div>
          <div className="text-xs text-gray-600 mb-0.5">سود خالص</div>
          <div className={`text-sm font-bold ${((viewItem.budget || 0) - (viewItem.cost || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency((viewItem.budget || 0) - (viewItem.cost || 0))}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">تاریخ شروع</div>
          <div className="text-gray-800 text-sm">{toShamsi(viewItem.startDate)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">تاریخ پایان</div>
          <div className="text-gray-800 text-sm">{toShamsi(viewItem.endDate)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">مدت باقیمانده</div>
          <div className={`text-sm ${
            viewItem.status === 'Completed' ? 'text-green-600' : 
            daysUntil(viewItem.endDate) < 0 ? 'text-red-600' : 
            daysUntil(viewItem.endDate) <= 7 ? 'text-yellow-600' : 'text-gray-600'
          }`}>
            {viewItem.status === 'Completed' ? (
              'تکمیل شده'
            ) : daysUntil(viewItem.endDate) < 0 ? (
              `${Math.abs(daysUntil(viewItem.endDate))} روز گذشته`
            ) : daysUntil(viewItem.endDate) === 0 ? (
              'امروز!'
            ) : (
              `${daysUntil(viewItem.endDate)} روز باقیمانده`
            )}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">شخص تماس</div>
          <div className="text-gray-800 text-sm">{viewItem.contactPerson || '---'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-0.5">تلفن تماس</div>
          <div className="text-gray-800 text-sm">{viewItem.contactPhone || '---'}</div>
        </div>
      </div>
      
      {viewItem.description && (
        <div>
          <div className="text-xs text-gray-600 mb-1">توضیحات</div>
          <p className="text-gray-600 text-sm bg-gray-50 rounded-xl p-3">{viewItem.description}</p>
        </div>
      )}
    </div>
  </Modal>
  
)}

      {invoiceItem && <InvoiceModal campaign={invoiceItem} onClose={() => setInvoiceItem(null)} todayDate={todayShamsi()} />}

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="حذف کمپین" message="آیا مطمئن هستید که می‌خواهید این کمپین را حذف کنید؟" />
      {showPaymentModal && (
  <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="پرداخت" size="sm">
    <div className="space-y-4">
      <div className="text-center">
        <div className="text-gray-800">کمپین: {viewItem?.title}</div>
        <div className="text-red-600 text-xl font-bold mt-2">
          {formatCurrency((viewItem?.budget || 0) - (viewItem?.paidAmount || 0))}
        </div>
        <div className="text-gray-500 text-sm">باقی‌مانده قابل پرداخت</div>
      </div>
      <input 
        type="number" 
        className="form-input" 
        placeholder="مبلغ پرداختی" 
        value={paymentAmount} 
        onChange={e => setPaymentAmount(e.target.value)}
      />
      <div className="flex gap-3 justify-end">
        <button onClick={() => setShowPaymentModal(false)} className="px-4 py-2 bg-gray-100 rounded-lg">انصراف</button>
        <button onClick={handleSponsorshipPayment} className="px-4 py-2 bg-green-600 rounded-lg">پرداخت</button>
      </div>
    </div>
  </Modal>
)}
    </div>
  );
}

function InvoiceModal({ campaign, onClose, todayDate }) {
  const remaining = (campaign.budget || 0) - (campaign.paidAmount || 0);

  const handlePrint = () => {
    const el = document.getElementById('sponsorship-invoice-print-area');
    const orig = document.body.innerHTML;
    document.body.innerHTML = el.innerHTML;
    window.print();
    document.body.innerHTML = orig;
    window.location.reload();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="فاکتور کمپین اسپانسرشیپ" size="lg">
      <div id="sponsorship-invoice-print-area">
        <div className="bg-white text-gray-900 rounded-xl p-8 font-sans" style={{ direction: 'rtl', fontFamily: 'Vazirmatn, Arial, sans-serif' }}>
          {/* Header */}
          <div className="flex items-start justify-between mb-8 pb-6 border-b-2 border-gray-200">
            <div>
              <div className="text-2xl font-bold text-blue-700">ویدا پرودکشن</div>
              <div className="text-gray-600 text-sm mt-1">Vida Production</div>
            </div>
            <div className="text-left">
              <div className="text-3xl font-bold text-gray-800">فاکتور</div>
              <div className="text-gray-600 text-sm mt-1">تاریخ: {todayDate}</div>
              <div className="text-gray-600 text-sm">شماره: INV-SP-{campaign._id?.slice(-6)?.toUpperCase()}</div>
            </div>
          </div>

          {/* Client & Campaign Info */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">مشتری / برند</div>
              <div className="text-lg font-bold text-gray-800">{campaign.client || '---'}</div>
              {campaign.contactPerson && <div className="text-gray-600 text-sm">{campaign.contactPerson}</div>}
              {campaign.contactPhone && <div className="text-gray-600 text-sm">{campaign.contactPhone}</div>}
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">کمپین</div>
              <div className="text-lg font-bold text-gray-800">{campaign.title}</div>
              <div className="text-gray-600 text-sm">پلتفرم: {campaign.platform}</div>
              <div className="text-gray-600 text-sm">تاریخ شروع: {toShamsi(campaign.startDate)}</div>
              <div className="text-gray-600 text-sm">تاریخ پایان: {toShamsi(campaign.endDate)}</div>
            </div>
          </div>

          {/* Services Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
            <thead>
              <tr style={{ backgroundColor: '#1e40af', color: 'white' }}>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px' }}>خدمات</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '13px' }}>مبلغ</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '12px 14px', fontSize: '14px' }}>
                  کمپین اسپانسرشیپ «{campaign.title}» - {campaign.platform}
                </td>
                <td style={{ padding: '12px 14px', textAlign: 'left', fontSize: '14px', fontWeight: 'bold' }}>{formatCurrency(campaign.budget)}</td>
              </tr>
              {campaign.description && (
                <tr style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' }}>
                  <td colSpan={2} style={{ padding: '10px 14px', fontSize: '13px', color: '#6b7280' }}>یادداشت: {campaign.description}</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ marginLeft: 'auto', width: '280px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb', fontSize: '14px' }}>
              <span style={{ color: '#6b7280' }}>مبلغ کل:</span>
              <span style={{ fontWeight: 'bold' }}>{formatCurrency(campaign.budget)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb', fontSize: '14px' }}>
              <span style={{ color: '#059669' }}>پرداخت شده:</span>
              <span style={{ color: '#059669', fontWeight: 'bold' }}>- {formatCurrency(campaign.paidAmount || 0)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', fontSize: '16px', fontWeight: 'bold', color: remaining > 0 ? '#dc2626' : '#059669' }}>
              <span>باقیات:</span>
              <span>{formatCurrency(remaining)}</span>
            </div>
          </div>

          <div className="flex justify-between mt-8 pt-4 border-t border-gray-200">
            <div className="text-center w-32">
              <div className="text-xs text-gray-500">مهر و امضاء شرکت</div>
              <div className="mt-6 border-t border-gray-300 pt-1 text-sm">...........................</div>
              <div className="text-[10px] text-gray-500">نام و امضاء</div>
            </div>
            <div className="text-center w-32">
              <div className="text-xs text-gray-500">امضاء مشتری</div>
              <div className="mt-6 border-t border-gray-300 pt-1 text-sm">...........................</div>
              <div className="text-[10px] text-gray-500">نام مشتری</div>
            </div>
            <div className="text-center w-32">
              <div className="text-xs text-gray-500">تاریخ</div>
              <div className="mt-6 text-sm">{todayDate}</div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>
            <p>از انتخاب شما متشکریم • ویدا پروداکشن</p>
            <p style={{ marginTop: '4px' }}>این فاکتور توسط سیستم مدیریت ویدا پرودکشن تولید شده است</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end mt-4 pt-4 border-t border-gray-200">
        <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-sm">بستن</button>
        <ActionBtn onClick={handlePrint} icon={<MdPrint size={16} />} label="چاپ / ذخیره PDF" variant="success" />
      </div>
    </Modal>
  );
}